import * as React from 'react';
import { jsx, css } from '@emotion/react';
import { mediaQueries, autoResponsiveClamp } from 'Src/me/pages/guest/proposals/new/React/viewHelpers';
import type { ContractorSearchInterfacePropsType } from '../..';
import { SparkleSvg } from './assets/Sparkle.svg';
import { useCurrentDevice } from '../../../../../../hooks/useCurrentDevice';
import { SpeechBubbleSvg } from './assets/SpeechBubble.svg';
import { BadgeSvg } from './assets/Badge.svg';
import { WalletSvg } from './assets/Wallet.svg';
import { ArrowSvg } from './assets/Arrow.svg';

const { tablet, mobile } = mediaQueries();

const KEY_NAME = 'SearchByReview';
const PARAM_NAME = 'preferredSearchCriteria';

export type SearchByReviewPropsType = Merge<ContractorSearchInterfacePropsType & {
  onApplyHandler: () => void;
}>;

export const SearchByReview = (props: SearchByReviewPropsType) => {
  const { userAnswers, commonAnswerHandler, onApplyHandler: onApplyHandlerFromProps } = props;
  const { isMobileView } = useCurrentDevice();
  const buttonElements = [
    {
      id: 1,
      iconElement: (
        <SpeechBubbleSvg />
      ),
      text: '口コミ・評判が良い',
    },
    {
      id: 2,
      iconElement: (
        <BadgeSvg />
      ),
      text: '施工件数が多い',
    },
    {
      id: 3,
      iconElement: (
        <WalletSvg />
      ),
      text: isMobileView ? 'コストパフォーマンスが良い' : 'コスパが良い',
    },
  ];
  const onApplyHandler = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const { value } = event.currentTarget;
    commonAnswerHandler(PARAM_NAME, value || '');

    userAnswers.push({
      keyName: 'searchByReview',
      paramName: PARAM_NAME,
      question: '重視したい業者の絞り込み条件は',
      answer: value || '',
      value: value || '',
    });

    onApplyHandlerFromProps();
  }, [commonAnswerHandler, onApplyHandlerFromProps, userAnswers]);

  return (
    <div
      css={css`
        padding: 50px 80px;
        background-color: #fff;
        border-radius: 20px;

        ${tablet(() => css`
          max-width: 568px;
          margin-inline: auto;
          padding: 0 1em;
          background-color: transparent;
        `)}
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 0 4px;
          margin-bottom: 30px;

          ${mobile(({ setAutomaticDesignBasedSize }) => css`
            margin-bottom: ${setAutomaticDesignBasedSize(30, 24)};
          `)}
        `}
      >
        <p
          css={css`
            color: #000;
            font-size: 20px;
            font-weight: bold;

            ${mobile(({ setAutomaticDesignBasedSize }) => css`
              font-size: ${setAutomaticDesignBasedSize(20, 18)};
            `)}
          `}
        >
          塗装業者を評判から探す
        </p>
        <div
          css={css`
            font-size: 0;
            width: 20px;

            ${mobile(({ setAutomaticDesignBasedSize }) => css`
              width: ${setAutomaticDesignBasedSize(20, 18)};
            `)}
          `}
        >
          <SparkleSvg />
        </div>
      </div>
      <div
        css={css`
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          place-content: center;
          gap: 0 22px;

          ${tablet(() => css`
            grid-template-columns: repeat(1, 1fr);
            gap: 0;
          `)}
        `}
      >
        {buttonElements.map((buttonElement) => {
          const { id, iconElement, text } = buttonElement;

          return (
            <button
              key={id}
              value={text}
              onClick={onApplyHandler}
              id={`ga_event_${KEY_NAME}_button_${id}`}
              css={css`
                color: #041156;
                font-size: 16px;
                font-weight: bold;
                text-align: center;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-direction: column;
                gap: 8px 0;
                height: 100%;
                padding: 12px 25px;
                background-color: #fff;
                border: 1px solid #2d334a;
                border-radius: 8px;
                position: relative;

                ${tablet(() => css`
                  flex-direction: row;
                  justify-content: flex-start;
                  gap: 0 30px;
                  height: 70px;
                  border-radius: 0;

                  &:first-of-type {
                    border-radius: 4px 4px 0 0;
                  }

                  &:last-of-type {
                    border-radius: 0 0 4px 4px;
                  }

                  &:not(:last-of-type) {
                    border-bottom: none;
                  }
                `)}

                ${mobile(({ setAutomaticDesignBasedSize }) => css`
                  font-size: ${setAutomaticDesignBasedSize(16, 16)};
                  gap: 0 ${setAutomaticDesignBasedSize(30, 24)};
                  height: ${setAutomaticDesignBasedSize(70, 60)};
                  padding: ${setAutomaticDesignBasedSize(12, 6)} ${setAutomaticDesignBasedSize(25, 21)};
                `)}

                &:hover {
                  background-color: #fff7c9;
                }
              `}
            >
              <span
                css={css`
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  height: 39px;

                  ${tablet(() => css`
                    height: auto;
                  `)}
                `}
              >
                <span
                  css={css`
                    font-size: 0;
                    display: block;
                    width: 30px;

                    ${mobile(({ setAutomaticDesignBasedSize }) => css`
                      width: ${setAutomaticDesignBasedSize(30, 30)};
                    `)}

                    ${id === 1 && css`
                      width: 39px;

                      ${mobile(({ setAutomaticDesignBasedSize }) => css`
                        width: ${setAutomaticDesignBasedSize(39, 35)};
                      `)}
                    `}

                    ${id === 2 && css`
                      ${mobile(({ setAutomaticDesignBasedSize }) => css`
                        width: ${setAutomaticDesignBasedSize(30, 28)};
                      `)}
                    `}
                  `}
                >
                  {iconElement}
                </span>
              </span>
              {text}
              <span
                css={css`
                  font-size: 0;
                  width: 9px;
                  position: absolute;
                  top: 50%;
                  right: 13px;
                  transform: translateY(-50%);

                  ${mobile(({ setAutomaticDesignBasedSize }) => css`
                    width: ${setAutomaticDesignBasedSize(9, 7)};
                    right: ${setAutomaticDesignBasedSize(13, 11)};
                  `)}
                `}
              >
                <ArrowSvg />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
